import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Heading2, Undo2, Redo2, Save } from 'lucide-react';

type Props = {
  value: string | null;
  readOnly?: boolean;
  placeholder?: string;
  autoSaveDelayMs?: number;
  onSave: (html: string) => Promise<void> | void;
  label?: string;
};

const toolBtn =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-900 hover:text-white dark:hover:bg-gray-700 transition-colors disabled:opacity-40';

/**
 * Reduzierter TipTap-Editor für Stunden-Inhalt / Hausaufgaben.
 * Auto-Save alle `autoSaveDelayMs` Millisekunden nach letzter Eingabe.
 */
export default function LessonContentEditor({
  value,
  readOnly = false,
  placeholder = 'Inhalt der Stunde…',
  autoSaveDelayMs = 5000,
  onSave,
  label
}: Props) {
  const [status, setStatus] = useState<'idle' | 'dirty' | 'saving' | 'saved' | 'error'>('idle');
  const timerRef = useRef<number | null>(null);
  const latestHtmlRef = useRef<string>(value || '');

  const editor = useEditor({
    extensions: [StarterKit],
    content: value || '<p></p>',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      latestHtmlRef.current = editor.getHTML();
      setStatus('dirty');
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        void flush();
      }, autoSaveDelayMs);
    }
  });

  const flush = async () => {
    if (!editor) return;
    const html = latestHtmlRef.current;
    try {
      setStatus('saving');
      await onSave(html);
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (err) {
      console.error('[LessonContentEditor] save failed', err);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '<p></p>', { emitUpdate: false });
      latestHtmlRef.current = value || '';
    }
  }, [value, editor]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          {label && <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3">{label}</span>}
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().toggleBold().run()}
            title="Fett"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            title="Kursiv"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Überschrift"
          >
            <Heading2 size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            title="Liste"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            title="Nummerierte Liste"
          >
            <ListOrdered size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().undo().run()}
            title="Rückgängig"
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly}
            onClick={() => editor?.chain().focus().redo().run()}
            title="Wiederherstellen"
          >
            <Redo2 size={16} />
          </button>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>
            {status === 'idle' && 'Bereit'}
            {status === 'dirty' && 'Wird gespeichert…'}
            {status === 'saving' && 'Speichere…'}
            {status === 'saved' && 'Gespeichert'}
            {status === 'error' && 'Fehler beim Speichern'}
          </span>
          <button
            type="button"
            className={toolBtn}
            disabled={readOnly || status === 'saving'}
            onClick={() => void flush()}
            title="Jetzt speichern"
          >
            <Save size={16} />
          </button>
        </div>
      </div>
      <div
        className="min-h-[160px] px-3 py-2 prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 [&_.ProseMirror]:min-h-[144px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:text-gray-900 dark:[&_.ProseMirror]:text-gray-100"
        data-placeholder={placeholder}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
